
/** Opening placeholder uses global tokens because the plan CSS is lazy.
 * The hero is neutral until the experience is resolved; it does not preview
 * either panel. Surface-3 keeps the bones visible in both themes.
 */
const Bone = ({
  className = "",
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) => (
  <div
    className={`animate-pulse rounded-md bg-[color:var(--sv-surface-3)] ${className}`}
    style={style}
  />
);

// Neutral while the server-owned experience and panel preference are unknown.
// Previewing a calorie arc here makes every Gema opening look like a UI update.
const PainelFantasma = () => (
  <div className="flex h-64 items-center justify-center" aria-hidden="true">
    <div className="flex items-center gap-2">
      {Array.from({length: 3}, (_, index) => <Bone key={index} className="h-2 w-2 rounded-full" />)}
    </div>
  </div>
);

const LinhaDeAlimento = () => (
  <div className="flex items-start justify-between gap-3 py-2.5">
    <div className="flex-1 space-y-1.5">
      <Bone className="h-4 w-2/5" />
      <Bone className="h-2.5 w-3/5" />
    </div>
    <Bone className="h-3.5 w-20 shrink-0" />
  </div>
);

const AppBootSkeleton = () => {
  /* A barra real esconde a Comunidade sem a flag, e some com a L.IA em
     `coleta_em_andamento`. A flag dá pra ler aqui; o status, não — é o que o
     skeleton está esperando resolver. Fica o caso comum. */
  const abas = 3;

  return (
    <div
      /* A retícula desde o primeiro quadro: ela é o chão do app, e aparecer só
         depois faria o fundo "ligar" no meio da transição. */
      className="app-reticula flex min-h-screen flex-col"
      style={{ paddingTop: "var(--sv-safe-top, 0px)" }}
      aria-hidden="true"
    >
      {/* ── A barra do topo ─────────────────────────────────────────────── */}
      <div
        className="mx-auto flex w-full max-w-[30rem] items-center justify-between"
        style={{
          minHeight: "4.875rem",
          padding: "var(--sv-header-topo) 0.75rem 0.75rem 1.125rem",
          background: "var(--sv-phone)",
        }}
      >
        <div className="space-y-2">
          {/* A saudação é Playfair grande; o "Seu plano de hoje", mono miúdo. */}
          <Bone className="h-7 w-48" />
          <Bone className="h-2.5 w-28" />
        </div>
        <div className="flex items-center gap-3">
          <Bone className="h-6 w-6 rounded-lg" />
          <Bone className="h-6 w-6 rounded-lg" />
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[30rem] flex-1 flex-col gap-2 px-3 pb-7 pt-1.5">
        <PainelFantasma />

        {/* ── A semana ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from({ length: 7 }).map((_, i) => (
            <Bone key={i} className="h-[3.1875rem] rounded-xl" />
          ))}
        </div>

        {/* ── O cartão da refeição ──────────────────────────────────────── */}
        <div
          className="px-4 pb-3 pt-4"
          style={{
            background: "var(--sv-vidro)",
            border: "1px solid var(--sv-border)",
            borderRadius: "var(--sv-radius-card)",
          }}
        >
          <div className="mb-3 flex items-center gap-3">
            <Bone className="h-8 w-8 rounded-lg" />
            <Bone className="h-5 w-40" />
            <Bone className="ml-auto h-3.5 w-16" />
          </div>
          {/* Quatro linhas: é quantas o café da manhã tem, e é o cartão mais
              alto do dia — o que evita a caixa encolher quando o real entrar. */}
          <div className="divide-y" style={{ borderColor: "var(--sv-border)" }}>
            <LinhaDeAlimento />
            <LinhaDeAlimento />
            <LinhaDeAlimento />
            <LinhaDeAlimento />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <Bone key={i} className="h-1.5 w-1.5 rounded-full" />
              ))}
            </div>
            <Bone className="h-3 w-16" />
          </div>
        </div>

        {/* ── Os três atalhos ───────────────────────────────────────────── */}
        <div
          className="grid grid-cols-3"
          style={{
            border: "1px solid var(--sv-border)",
            borderRadius: "var(--sv-radius-card)",
            background: "var(--sv-vidro)",
          }}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex h-[3.125rem] items-center justify-center gap-2">
              <Bone className="h-4 w-4 rounded-full" />
              <Bone className="h-3 w-14" />
            </div>
          ))}
        </div>
      </div>

      {/* ── As abas ───────────────────────────────────────────────────────
          Sem fio no topo e com `glass-panel`, como a `BottomNav` de verdade:
          um fio aqui apareceria e sumiria na troca. */}
      <nav
        className="glass-panel fixed bottom-0 left-0 right-0 z-40"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex h-12 items-center justify-around">
          {Array.from({ length: abas }).map((_, i) => (
            <div key={i} className="flex h-full flex-1 flex-col items-center justify-center gap-1">
              <Bone className="h-4 w-4 rounded-md" />
              <Bone className="h-2 w-9" />
            </div>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default AppBootSkeleton;
